import React, { useState, useEffect } from 'react';
import { Field } from 'react-final-form';

// Hooks
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Utils
import { createFormField } from 'Shared/utils/createFormField';
import { required } from 'Shared/utils/formValidations';

// Types
import { GoalMetricTypeType } from 'Shared/constants/types';

// Components
import RadioInput from 'Shared/components/RadioInput/RadioInput';
import FieldGroup from 'Shared/components/FormComponents/FieldGroup/FieldGroup';
import FormSelect from 'Shared/components/FormComponents/FormSelect';
import FormInput from 'Shared/components/FormComponents/FormInput';

// Styles
import './FieldMetricType.scss';

type Props = {
  onChange: Function;
  value: GoalMetricTypeType;
  inSetting?: boolean;
};

type MetricType = {
  label: string;
  value: GoalMetricTypeType | 'other';
};

const FieldMetricType = ({ onChange, value, inSetting = false }: Props) => {
  const { tr } = useTranslation();
  const [initialValue] = useState<GoalMetricTypeType>(value);
  const [selectedOption, setSelectedOption] = useState<GoalMetricTypeType>(null);
  const [showCustomType, setShowSelectedCustomType] = useState<boolean>(false);

  const options: Array<MetricType> = [
    { label: tr('perform.goals.tracking.metric_type.completing_sub_goals'), value: GoalMetricTypeType.sub_goals },
    { label: tr('perform.goals.tracking.metric_type.completing_action_items'), value: GoalMetricTypeType.action_items },
    { label: tr('perform.goals.tracking.metric_type.tracking_number'), value: 'other' },
  ];

  const otherOptions: Array<MetricType> = [
    { label: tr('perform.goals.tracking.metric_option.number'), value: GoalMetricTypeType.number },
    { label: tr('perform.goals.tracking.metric_option.percentage'), value: GoalMetricTypeType.percent },
    { label: tr('perform.goals.tracking.metric_option.dollar_amount'), value: GoalMetricTypeType.dollar_amount },
  ];

  const handleOptionClick = (option: MetricType) => {
    if (option.value === 'other') {
      if (
        initialValue === GoalMetricTypeType.number ||
        initialValue === GoalMetricTypeType.percent ||
        initialValue === GoalMetricTypeType.dollar_amount
      ) {
        setSelectedOption(initialValue);
        onChange(initialValue);
      } else {
        setSelectedOption(null);
        onChange(null);
      }

      setShowSelectedCustomType(true);
    } else {
      onChange(option.value);
      setSelectedOption(option.value);
      setShowSelectedCustomType(false);
    }
  };

  const handleSelectOtherMetricType = (option: GoalMetricTypeType) => {
    onChange(option);
    setSelectedOption(option);
  };

  useEffect(() => {
    if (value) {
      setSelectedOption(value);

      if (
        value === GoalMetricTypeType.number ||
        value === GoalMetricTypeType.percent ||
        value === GoalMetricTypeType.dollar_amount
      ) {
        setShowSelectedCustomType(true);
      }
    }
  }, []);

  const baseClassName = 'FieldMetricType';

  return (
    <div className={baseClassName}>
      {options.map(option => (
        <RadioInput
          key={option.value}
          name={option.value}
          label={option.label}
          className="mt-1 mt-sm-0"
          checked={(selectedOption && selectedOption === option.value) || (option.value === 'other' && showCustomType)}
          onClick={() => handleOptionClick(option)}
        />
      ))}
      {showCustomType && (
        <div
          className={`${baseClassName}__other-tracking ${inSetting ? `${baseClassName}__other-tracking--column` : ''}`}
        >
          <FieldGroup
            label={tr('perform.goals.tracking.what_tracking_label')}
            labelClassName={`${baseClassName}__label`}
            labelRequired={tr('perform.goals.form.required_indicator')}
            className={`${baseClassName}__other-tracking__what ${baseClassName}__field`}
          >
            <FormSelect
              options={otherOptions}
              placeholder={tr('perform.goals.tracking.what_tracking_placeholder')}
              onChange={handleSelectOtherMetricType}
              value={selectedOption}
              menuPortalTarget={document.body}
              customStyles={{
                menuPortal: base => ({ ...base, zIndex: '1099' }),
              }}
            />
          </FieldGroup>
          {inSetting && (
            <FieldGroup
              label={tr('perform.goals.tracking.initial_value_label')}
              labelClassName={`${baseClassName}__label`}
              className={`${baseClassName}__other-tracking__what__input`}
              required
              labelRequired={tr('perform.goals.form.required_indicator')}
            >
              <Field
                name="metricInitialValue"
                component={FormInput}
                placeholder={tr('perform.goals.tracking.initial_value_placeholder')}
                type="number"
                validate={required}
              />
            </FieldGroup>
          )}
          <FieldGroup
            label={tr('perform.goals.tracking.current_value_label')}
            labelClassName={`${baseClassName}__label`}
            className={`${baseClassName}__other-tracking__what__input`}
            required
            labelRequired={tr('perform.goals.form.required_indicator')}
          >
            <Field
              name="metricCurrentValue"
              component={FormInput}
              placeholder={tr('perform.goals.tracking.current_value_placeholder')}
              type="number"
              validate={required}
            />
          </FieldGroup>
          <FieldGroup
            label={tr('perform.goals.tracking.goal_value_label')}
            labelClassName={`${baseClassName}__label`}
            className={`${baseClassName}__other-tracking__what__input`}
            required
            labelRequired={tr('perform.goals.form.required_indicator')}
          >
            <Field
              name="metricGoalValue"
              component={FormInput}
              placeholder={tr('perform.goals.tracking.goal_value_placeholder')}
              type="number"
              validate={required}
            />
          </FieldGroup>
        </div>
      )}
    </div>
  );
};

export default createFormField(FieldMetricType);
